import React, { useMemo } from 'react';
import { Chip } from '@mui/material';
import { keyframes } from '@emotion/react';
import { Upload, Cached, HelpOutline, CheckCircleOutline, CancelOutlined, Edit, Block } from '@mui/icons-material';

const spin = keyframes`
    from {
        transform: rotate(360deg);
    }
    to {
        transform: rotate(0deg);
    }
`;

const STATUS_MAP = {
    uploaded: {
        label: 'Uploaded',
        icon: <Upload />,
        color: 'default',
    },
    processed: {
        label: 'Processed',
        icon: <CheckCircleOutline />,
        color: 'info',
    },
    processing: {
        label: 'Extraction In Progress',
        icon: <Cached />,
        color: 'info',
    },
    failed: {
        label: 'Failed',
        icon: <CancelOutlined />,
        color: 'error',
    },
    content_extraction_in_progress: {
        label: 'Extraction In Progress',
        icon: <Cached />,
        color: 'warning',
    },
    content_extraction_completed: {
        label: 'Extraction Completed',
        icon: <CheckCircleOutline />,
        color: 'success',
    },
    content_extraction_failed: {
        label: 'Extraction Failed',
        icon: <CancelOutlined />,
        color: 'error',
    },
    completed: {
        label: 'Completed',
        icon: <CheckCircleOutline />,
        color: 'success',
    },
    pending: {
        label: 'Pending',
        icon: <Cached />,
        color: 'warning',
    },
    approved: {
        label: 'Validated',
        icon: <CheckCircleOutline />,
        color: 'success',
    },
    rejected: {
        label: 'Rejected',
        icon: <CancelOutlined />,
        color: 'error',
    },
    in_progress: {
        label: 'In Progress',
        icon: <Cached />,
        color: 'warning',
    },
    dispatched: {
        label: 'Extraction In Progress',
        icon: <Cached />,
        color: 'warning',
    },
    queued: {
        label: 'Extraction In Progress',
        icon: <Cached />,
        color: 'warning',
    },
    file_progress: {
        label: 'File Validation In Progress',
        icon: <Cached />,
        color: 'warning',
    },
    file_failed: {
        label: 'Failed',
        icon: <CancelOutlined />,
        color: 'error',
    },
    extraction_progress: {
        label: 'Extraction In Progress',
        icon: <Cached />,
        color: 'warning',
    },
    extraction_completed: {
        label: 'Extraction Completed',
        icon: <CheckCircleOutline />,
        color: 'success',
    },
    extraction_failed: {
        label: 'Extraction Failed',
        icon: <CancelOutlined />,
        color: 'error',
    },
    active: {
        label: 'Active',
        icon: <CheckCircleOutline />,
        color: 'success',
    },
    draft: {
        label: 'Draft',
        icon: <Edit />,
        color: 'warning',
    },
    error: {
        label: 'Error',
        icon: <CancelOutlined />,
        color: 'error',
    },
    inactive: {
        label: 'Inactive',
        icon: <Block />,
        color: 'default',
    },
};

const StatusBadge = ({ status, type, sx = {} }) => {
    const info = useMemo(() => {
        if (STATUS_MAP[status]) return STATUS_MAP[status];

        const FILE_STATUSES = {
            progress: ['uploaded', 'processing', 'processed'],
            failed: ['failed'],
        };

        const EXTRACTION_STATUSES = {
            progress: ['content_extraction_in_progress', 'pending', 'in_progress', 'dispatched', 'queued'],
            completed: ['content_extraction_completed', 'completed'],
            failed: ['failed', 'content_extraction_failed'],
        };

        if (type === 'file') {
            if (FILE_STATUSES.progress.includes(status)) return STATUS_MAP.file_progress;
            if (FILE_STATUSES.failed.includes(status)) return STATUS_MAP.file_failed;
        } else if (type === 'extraction') {
            if (EXTRACTION_STATUSES.progress.includes(status)) return STATUS_MAP.extraction_progress;
            if (EXTRACTION_STATUSES.completed.includes(status)) return STATUS_MAP.extraction_completed;
            if (EXTRACTION_STATUSES.failed.includes(status)) return STATUS_MAP.extraction_failed;
        }

        return {
            label: status,
            icon: <HelpOutline />,
            color: 'default',
        };
    }, [status, type]);

    const spinningStatuses = [
        'processing',
        'content_extraction_in_progress',
        'in_progress',
        'dispatched',
        'queued',
        'file_progress',
        'extraction_progress',
        'pending',
    ];

    // Memoize icon
    const icon = useMemo(() => {
        if (spinningStatuses.includes(status)) {
            return React.cloneElement(info.icon, {
                sx: {
                    animation: `${spin} 2s linear infinite`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            });
        }
        return info.icon;
    }, [status, info.icon]);

    return <Chip icon={icon} label={info.label} color={info.color} variant="filled" size="medium" sx={sx} />;
};

export default React.memo(StatusBadge);
